import { useEffect, useRef, useState } from "react";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import LabClinicModalComponent from "../LabClinicModalComponent";
import { useClinicLabPartnersAPI } from "@/app/hooks/api_hooks/LabReport/useClinicLabPartnersAPI";
import { useLabClinicMappingAPI } from "@/app/hooks/api_hooks/LabReport/useLabClinicMappingAPI";
import { useLabPartnerListAPI } from "@/app/hooks/api_hooks/LabReport/useLabPartnerListAPI";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableRow from "./SortableRow";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useUtils } from "@/app/hooks/useUtils";
import { useConfigurationClinicDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicDetailAPI";
import RemoveLabPartnerModal from "../RemoveLabPartnerModal";

const LabPartnerClinicMapping = () => {
  const { execute: fetchLabPartners, labPartners } = useClinicLabPartnersAPI();
  const { execute: fetchLabPartnersGlobal, labList } = useLabPartnerListAPI();
  const [isEditMode, setIsEditMode] = useState(false);
  const { execute: createMapping } = useLabClinicMappingAPI();
  const { formatTo12Hour } = useUtils();
  const [clinicName, setClinicName] = useState<string>("");
  const { execute: fetchClinicDetail, clinicDetails } = useConfigurationClinicDetailAPI();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);


  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      lab_clinic_mapping: [],
    },
  });

  const labClinicMapping = useFieldArray<any>({
    name: "lab_clinic_mapping",
    control: formHook.control,
  });

  const [params, _setParams] = useSearchParams();

  const clinic_id = params.get("clinic_id");

  useEffect(() => {
    if (!clinic_id) return;
    fetchClinicDetail(clinic_id);
  }, [clinic_id, fetchClinicDetail]);

  // console.log(clinicDetails, "clinicDetails");

  useEffect(() => {
    if (clinicDetails?.clinic_data?.name) {
      setClinicName(clinicDetails?.clinic_data?.name);
    }
  }, [clinicDetails]);


  useEffect(() => {
    if (clinic_id) {
      fetchLabPartners(clinic_id).then(({ data, status }) => {
        if (status && data?.length > 0) {
          setOpen(false);
          navigate(`/app/clinic-lab-report/lab-partner-clinic-mapping?clinic_id=${clinic_id}&is_clinic_mapping=false`);
        } else if (status && data.length === 0) {
          navigate(`/app/clinic-lab-report/lab-partner-clinic-mapping?clinic_id=${clinic_id}&is_clinic_mapping=true`);
          setOpen(true);
        } else {
          setOpen(false);
        }
      });
    }
  }, [clinic_id]);

  const mapPickupTimingsToSlots = (slots = []) => {
    return slots
      .filter((t) => t.start_time_hour && t.start_time_minute && t.end_time_hour && t.end_time_minute)
      .map((t) => ({
        start_time: `${t.start_time_hour}:${t.start_time_minute}`,
        end_time: `${t.end_time_hour}:${t.end_time_minute}`,
      }));
  };

  const isInitialized = useRef(false);

  useEffect(() => {
    if (labPartners?.length > 0 && !isInitialized.current) {
      const initialized = labPartners.map((lab) => ({
        ...lab,
        slots:
          lab.slots?.length > 0
            ? lab.slots.map((slot) => {
              const [startHour = "", startMinute = ""] = slot.start_time?.split(":") || [];
              const [endHour = "", endMinute = ""] = slot.end_time?.split(":") || [];

              return {
                start_time_hour: startHour,
                start_time_minute: startMinute,
                end_time_hour: endHour,
                end_time_minute: endMinute,
              };
            })
            : [
              {
                start_time_hour: "",
                start_time_minute: "",
                end_time_hour: "",
                end_time_minute: "",
              },
            ],
      }));


      labClinicMapping.replace(initialized);
      isInitialized.current = true;
    }
  }, [labPartners]);

  const handelAddLabPartner = (data) => {
    const currentLabs = formHook?.getValues("lab_clinic_mapping") || [];
    const result = data?.map((id) => ({ lab_id: id }));

    const mergedData = [...currentLabs, ...(result || [])];

    const constructedBody = {
      labs: mergedData.map((lab, index) => ({
        ...lab,
        priority: index + 1,
        slots: mapPickupTimingsToSlots(lab.slots),
      })),
    };
    createMapping(constructedBody, clinic_id).then(({ status }) => {
      if (status) {
        setOpen(false);
        navigate(
          `/app/clinic-lab-report/lab-partner-clinic-mapping?clinic_id=${clinic_id}&is_clinic_mapping=false`,
        );
        fetchLabPartners(clinic_id);
        isInitialized.current = false;
      }
    });
  };

  useEffect(() => {
    fetchLabPartnersGlobal();
  }, []);

  const handelSubmit = (data: any) => {
    const labs = data?.lab_clinic_mapping || [];
    const constructedBody = {
      labs: labs.map((lab, index) => ({
        ...lab,
        priority: index + 1,
        slots: mapPickupTimingsToSlots(lab.slots),
      })),
    };

    createMapping(constructedBody, clinic_id).then(({ status }) => {
      if (status) {
        setIsEditMode(false);
      }
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Use fields array directly for accurate index lookup
    const oldIndex = labClinicMapping.fields.findIndex((item: any) => item.id === active.id);
    const newIndex = labClinicMapping.fields.findIndex((item: any) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Move item
    labClinicMapping.move(oldIndex, newIndex);

    // Update priorities after the move
    setTimeout(() => {
      const updated = formHook.watch("lab_clinic_mapping")?.map((item: any, index: number) => ({
        ...item,
        priority: index + 1,
      }));

      formHook.setValue("lab_clinic_mapping", updated);
    }, 0);
  };

  const hourList = Array.from({ length: 24 }, (_, i) => ({
    id: String(i).padStart(2, "0"),
    title: String(i).padStart(2, "0"),
  }));

  const generateMinutes = (step: number) =>
    Array.from({ length: 60 / step }, (_, i) => {
      const value = String(i * step).padStart(2, "0");
      return { id: value, title: value };
    });

  const minuteList = generateMinutes(5);


  const buildTime = (h?: string, m?: string) => {
    if (!h || !m) return null;
    return `${h}:${m}`;
  };

  return (
    <div className="bg-white">
      <div className="bg-white p-[10px] rounded-lg ">
        {labClinicMapping?.fields.length > 0 && (
          <div className="rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
            <h1 className="font-semibold text-base text-[#000000] px-3 pt-3 pb-[10px]">
              {clinicName}
            </h1>
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[calc(100vh-220px)]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <table className="w-full min-w-[900px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] sticky top-0 z-10 font-normal">
                        <tr>
                          {isEditMode && <th className="px-3 py-3 text-left text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap"></th>}
                          <th className="px-3 py-3 text-center text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">Preference</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">Lab Partner Name</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">No. of Tests</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">Distance</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">
                            Pick Up Time (Start time + End Time)
                          </th>

                          {isEditMode && <th className="px-3 py-3 text-center text-xs font-medium text-[#1F1F23] bg-[#F5F5F5] whitespace-nowrap">Actions</th>}
                        </tr>
                      </thead>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={labClinicMapping?.fields?.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody className="overflow-hidden rounded-xl">
                    {labClinicMapping?.fields?.map((item, index) => {
                      const isFirst = index === 0;
                      const isLast = index === labClinicMapping?.fields?.length - 1;
                      const isSingle = labClinicMapping?.fields?.length === 1;
                      const slots =
                        formHook.watch(`lab_clinic_mapping.${index}.slots`)?.length > 0
                          ? formHook.watch(`lab_clinic_mapping.${index}.slots`)
                          : [
                            {
                              start_time_hour: "",
                              start_time_minute: "",
                              end_time_hour: "",
                              end_time_minute: "",
                            },
                          ];

                      return (
                        <SortableRow
                          key={item.id}
                          id={item.id}
                          isFirst={isFirst}
                          isSingle={isSingle}
                          isLast={isLast}
                          isEditMode={isEditMode}
                        >
                          <td
                            className={`px-3 py-3 text-center ${!isEditMode && isSingle
                              ? "rounded-tl-xl rounded-bl-xl"
                              : !isEditMode
                                ? `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-bl-xl" : ""}`.trim()
                                : ""
                              }`}
                          >
                            {formHook.watch(`lab_clinic_mapping.${index}.priority`)}
                          </td>

                          <td className="p-2 text-left">{formHook.watch(`lab_clinic_mapping.${index}.lab_name`)}</td>

                          <td className="p-2 text-center">
                            {formHook.watch(`lab_clinic_mapping.${index}.no_of_test`)}
                          </td>

                          <td className="p-2 text-center">
                            {formHook.watch(`lab_clinic_mapping.${index}.distance`) || 0} km
                          </td>

                          <td className="p-2 text-center whitespace-nowrap min-w-[420px]">
                            {!isEditMode ? (
                              /* ================= VIEW MODE ================= */
                              <div className="text-sm text-gray-800 space-y-1">
                                {slots.map((t, i) => {
                                  const startTime = buildTime(t.start_time_hour, t.start_time_minute);
                                  const endTime = buildTime(t.end_time_hour, t.end_time_minute);

                                  if (!startTime || !endTime) return null;

                                  return (
                                    <div key={i}>
                                      {formatTo12Hour(startTime)} – {formatTo12Hour(endTime)}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              /* ================= EDIT MODE ================= */
                              <div className="flex flex-col">
                                {slots.map((slot, timeIndex) => (
                                  <div key={timeIndex} className="mb-2 flex flex-nowrap items-center gap-3">
                                    {/* START TIME */}
                                    <div className="grid grid-cols-2 gap-2 shrink-0">
                                      <NonSearchableSelectMenu
                                        placeholder="HH"
                                        selectItems={hourList}
                                        register={formHook.register(
                                          `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_hour`,
                                          { required: true },
                                        )}
                                        defaultSelected={
                                          hourList?.filter(
                                            (oc) =>
                                              oc.title ===
                                              formHook.watch(
                                                `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_hour`,
                                              ),
                                          )[0]
                                        }
                                        onSelectItem={(item) =>
                                          formHook.setValue(
                                            `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_hour`,
                                            item.id,
                                            { shouldDirty: true },
                                          )
                                        }
                                        containerClassName="w-20"
                                        fieldError={formHook.formState.errors?.lab_clinic_mapping?.[index]?.slots?.[timeIndex]?.start_time_hour}
                                        errorMessages={[{ type: "required", message: "" }]}
                                      />

                                      <NonSearchableSelectMenu
                                        placeholder="MM"
                                        selectItems={minuteList}
                                        register={formHook.register(
                                          `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_minute`,
                                          { required: true },
                                        )}
                                        onSelectItem={(item) =>
                                          formHook.setValue(
                                            `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_minute`,
                                            item.id,
                                            { shouldDirty: true },
                                          )
                                        }
                                        defaultSelected={
                                          hourList?.filter(
                                            (oc) =>
                                              oc.title ===
                                              formHook.watch(
                                                `lab_clinic_mapping.${index}.slots.${timeIndex}.start_time_minute`,
                                              ),
                                          )[0]
                                        }
                                        containerClassName="w-20"
                                        fieldError={formHook.formState.errors?.lab_clinic_mapping?.[index]?.slots?.[timeIndex]?.start_time_minute}
                                        errorMessages={[{ type: "required", message: "" }]}
                                      />
                                    </div>

                                    {/* END TIME */}
                                    <div className="grid grid-cols-2 gap-2 shrink-0">
                                      <NonSearchableSelectMenu
                                        placeholder="HH"
                                        selectItems={hourList}
                                        register={formHook.register(
                                          `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_hour`,
                                          { required: true },
                                        )}
                                        onSelectItem={(item) =>
                                          formHook.setValue(
                                            `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_hour`,
                                            item.id,
                                            { shouldDirty: true },
                                          )
                                        }
                                        defaultSelected={
                                          hourList?.filter(
                                            (oc) =>
                                              oc.title ===
                                              formHook.watch(
                                                `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_hour`,
                                              ),
                                          )[0]
                                        }
                                        containerClassName="w-20"
                                        fieldError={formHook.formState.errors?.lab_clinic_mapping?.[index]?.slots?.[timeIndex]?.end_time_hour}
                                        errorMessages={[{ type: "required", message: "" }]}
                                      />

                                      <NonSearchableSelectMenu
                                        placeholder="MM"
                                        selectItems={minuteList}
                                        register={formHook.register(
                                          `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_minute`,
                                          { required: true },
                                        )}
                                        defaultSelected={
                                          hourList?.filter(
                                            (oc) =>
                                              oc.title ===
                                              formHook.watch(
                                                `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_minute`,
                                              ),
                                          )[0]
                                        }
                                        onSelectItem={(item) =>
                                          formHook.setValue(
                                            `lab_clinic_mapping.${index}.slots.${timeIndex}.end_time_minute`,
                                            item.id,
                                            { shouldDirty: true },
                                          )
                                        }
                                        containerClassName="w-20"
                                        fieldError={formHook.formState.errors?.lab_clinic_mapping?.[index]?.slots?.[timeIndex]?.end_time_minute}
                                        errorMessages={[{ type: "required", message: "" }]}
                                      />
                                    </div>

                                    {/* ACTION BUTTONS */}
                                    {timeIndex === slots.length - 1 && (
                                      <div className="flex gap-2 w-[56px] shrink-0 justify-end">
                                        <button
                                          type="button"
                                          className={`p-1 border rounded ${timeIndex === 0 ? "invisible" : ""}`}
                                          onClick={() =>
                                            formHook.setValue(
                                              `lab_clinic_mapping.${index}.slots`,
                                              slots.filter((_, i) => i !== timeIndex),
                                              { shouldDirty: true },
                                            )
                                          }
                                        >
                                          <MinusIcon className="w-4 h-4" />
                                        </button>

                                        <button
                                          type="button"
                                          className="p-1 border rounded"
                                          onClick={() =>
                                            formHook.setValue(
                                              `lab_clinic_mapping.${index}.slots`,
                                              [
                                                ...slots,
                                                {
                                                  start_time_hour: "",
                                                  start_time_minute: "",
                                                  end_time_hour: "",
                                                  end_time_minute: "",
                                                },
                                              ],
                                              { shouldDirty: true },
                                            )
                                          }
                                        >
                                          <PlusIcon className="w-4 h-4" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>

                          <td className="pl-4 sm:pl-2 pr-2 py-2 text-center">
                            {isEditMode && (
                              <button
                                className="w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ml-2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setRemoveIndex(index);
                                  setOpenRemoveModal(true);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="white"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-5 text-[#ED0101]"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                  />
                                </svg>
                              </button>
                            )}
                          </td>

                          <td
                            className={`p-2 text-center flex justify-center ${isSingle
                              ? "rounded-tr-xl rounded-br-xl"
                              : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                              }`}
                          ></td>
                        </SortableRow>
                      );
                    })}
                  </tbody>
                </SortableContext>
              </DndContext>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-between px-2 pb-2">
        {!isEditMode ? (
          <button className=""> </button>
        ) : (
          <button onClick={() => setOpen(true)} className={`${SECONDARY_BUTTON} my-2 border-dotted`}>
            <PlusIcon className="w-4 h-4" /> Add Lab Partner
          </button>
        )}
        <button
          onClick={() => {
            if (isEditMode) {
              formHook.handleSubmit(handelSubmit)();
            } else {
              setIsEditMode(true);
            }
          }}
          className={isEditMode ? `${PRIMARY_BUTTON} my-2` : `${SECONDARY_BUTTON} my-2`}
        >
          {isEditMode ? "Save" : "Edit"}
        </button>
      </div>
      {open && (
        <LabClinicModalComponent
          open={open}
          setOpen={setOpen}
          clinicId={clinic_id}
          labList={labList}
          labPartners={labPartners}
          handelAddLabPartner={handelAddLabPartner}
        />
      )}

      <RemoveLabPartnerModal
        open={openRemoveModal}
        setOpen={setOpenRemoveModal}
        loading={false}
        onConfirm={() => {
          if (removeIndex !== null) {
            labClinicMapping.remove(removeIndex);
          }
          setRemoveIndex(null);
          setOpenRemoveModal(false);
        }}
      />
    </div>
  );
};

export default LabPartnerClinicMapping;
